import { useCallback } from 'react';
import { formatGraphqlErrors } from 'utils/helpers';
import { useIntl } from 'react-intl';
import cloneDeep from 'lodash/cloneDeep';
import { useMutation } from '@apollo/client/react';
import {
  ChangeBudgetStepDocument,
  MyBudgetDocument,
  type MyBudgetQuery,
  type MyBudgetQueryVariables,
} from 'graphql/generated/graphql';
import requireValue from 'utils/requireValue';

const useChangeBudgetStep = (): [(step: number) => Promise<void>, boolean] => {
  const [changeBudgetStepMutation, { client, loading }] = useMutation(ChangeBudgetStepDocument);
  const { locale } = useIntl();

  const handleChangeStep = useCallback(
    async (step: number) => {
      try {
        const { data } = await changeBudgetStepMutation({ variables: { step } });
        const { changeBudgetStep } = requireValue(data, 'changeBudgetStep');

        if (!changeBudgetStep.success) return;

        const budget = client.readQuery<MyBudgetQuery, MyBudgetQueryVariables>({
          query: MyBudgetDocument,
          variables: { locale },
        });
        const clonedBudget = requireValue(cloneDeep(budget), 'myBudget');
        requireValue(clonedBudget.myBudget, 'step').step = step;
        client.writeQuery<MyBudgetQuery, MyBudgetQueryVariables>({
          query: MyBudgetDocument,
          variables: { locale },
          data: clonedBudget,
        });
      } catch (error) {
        const e = formatGraphqlErrors(error);
        throw e;
      }
    },
    [changeBudgetStepMutation, client, locale],
  );

  return [handleChangeStep, loading];
};

export default useChangeBudgetStep;
